'use client'

import { LineClose } from '@qr-front/icons/line-close'
import { SolidAlertCircle } from '@qr-front/icons/solid-alert-circle'
import { SolidCheckCircle } from '@qr-front/icons/solid-check-circle'
import { SolidCloseCircle } from '@qr-front/icons/solid-close-circle'
import { SolidInfoCircle } from '@qr-front/icons/solid-info-circle'
import { cn } from '@qr-front/ui/utils'
import { useTranslations } from 'next-intl'
import { ReactElement } from 'react'
import type {
	IconProps,
	Slide as ToastSlide,
	toast,
	ToastContent,
} from 'react-toastify'

export type TNotificationType = 'success' | 'error' | 'info' | 'warn'

export type TToastContext = 'default' | 'generator'

// Lazy loading state for react-toastify dependencies
let toastifyLoaded = false
let toastifyPromise: Promise<{
	toast: typeof toast
	Slide: typeof ToastSlide
}> | null = null

// Lazy loader function for react-toastify dependencies
const loadToastify = async () => {
	if (toastifyLoaded) {
		return toastifyPromise
	}

	if (!toastifyPromise) {
		toastifyPromise = import('react-toastify').then((module) => {
			toastifyLoaded = true
			return {
				toast: module.toast,
				Slide: module.Slide,
			}
		})
	}

	return toastifyPromise
}

const toastBodyCls = cn('relative bg-white font-body')

const TYPE_TO_ACCENT: Record<TNotificationType, string> = {
	success: 'var(--success-color)',
	error: 'var(--error-color)',
	info: 'var(--info-color)',
	warn: 'var(--warning-color)',
}

const ToastBody = ({
	type,
	message,
}: {
	type: TNotificationType
	message: string
}) => {
	const t = useTranslations()

	const TYPE_TO_HEADING: Record<TNotificationType, string> = {
		success: t('ui__toast__title__success'),
		error: t('ui__toast__title__error'),
		info: t('ui__toast__title__info'),
		warn: t('ui__toast__title__warning'),
	}

	return (
		<div className={toastBodyCls}>
			<div className="flex flex-col">
				<span className="type-body-medium font-semibold text-[var(--accent)]">
					{TYPE_TO_HEADING[type]}
				</span>
				<span className="type-body-small text-dark-grey">
					{message}
				</span>
			</div>
		</div>
	)
}

const ErrorToast = (message: string) => (
	<ToastBody type="error" message={message} />
)

const InfoToast = (message: string) => (
	<ToastBody type="info" message={message} />
)

const SuccessToast = (message: string) => (
	<ToastBody type="success" message={message} />
)

const WarningToast = (message: string) => (
	<ToastBody type="warn" message={message} />
)

const toastTypeMapping: Record<
	TNotificationType,
	(message: string) => ReactElement
> = {
	success: SuccessToast,
	error: ErrorToast,
	info: InfoToast,
	warn: WarningToast,
}

const iconMapping: Record<TNotificationType, ReactElement<IconProps>> = {
	success: (
		<SolidCheckCircle className="shrink-0 [color:var(--success-color)]" />
	),
	error: <SolidCloseCircle className="shrink-0 text-[var(--error-color)]" />,
	info: <SolidInfoCircle className="shrink-0 text-[var(--info-color)]" />,
	warn: <SolidAlertCircle className="shrink-0 text-[var(--warning-color)]" />,
}

export const showNotification = async (
	message: ToastContent,
	{
		type,
		context = 'default',
		onClose,
		toastId,
		containerId = 'notification',
	}: {
		type: TNotificationType
		context?: TToastContext
		onClose?: () => void
		toastId?: string
		containerId?: string
	},
) => {
	try {
		// Lazy load react-toastify dependencies
		const toastifyModule = await loadToastify()
		if (!toastifyModule) {
			throw new Error('Failed to load react-toastify module')
		}
		const { toast, Slide } = toastifyModule

		const toastIdInner =
			toastId || (typeof message === 'string' ? message : undefined)
		const messageText =
			typeof message === 'string' ? message : String(message)
		const toastContent = toastTypeMapping[type]

		toast[type](toastContent(messageText), {
			...(containerId ? { containerId } : {}),
			position: context === 'generator' ? 'bottom-right' : 'top-center',
			autoClose: 5000,
			icon: iconMapping[type],
			hideProgressBar: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			toastId: toastIdInner,
			theme: 'light',
			transition: (props: Parameters<typeof ToastSlide>[0]) => (
				<Slide
					{...props}
					position={
						context === 'generator'
							? 'bottom-center'
							: props.position
					}
				/>
			),
			style: {
				'--accent': TYPE_TO_ACCENT[type],
			},
			className: cn(
				cn(
					`context-${context}`,
					'!mb-0 flex w-full gap-4 !rounded-none p-4 pb-3 !shadow-generator [border-color:var(--accent)] tablet:border-l-4 tablet:pb-4 tablet:pl-3',
					context === 'generator'
						? 'border-t-4 tablet:border-t-0'
						: 'border-b-4 tablet:border-b-0',
				),
			),
			onClose,
			closeButton: ({ closeToast }: { closeToast: () => void }) => (
				<button
					className="absolute right-0 top-0 p-2"
					onClick={closeToast}
				>
					<LineClose className="size-3 text-dark-grey" />
				</button>
			),
		})
	} catch (error) {
		console.error('Failed to load react-toastify:', error)
	}
}

export const dismissAllNotifications = async () => {
	try {
		const toastifyModule = await loadToastify()
		if (!toastifyModule) {
			throw new Error('Failed to load react-toastify module')
		}
		const { toast } = toastifyModule
		toast.dismiss()
	} catch (error) {
		console.error('Failed to load react-toastify:', error)
	}
}
